import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import HandshakeIcon from '@mui/icons-material/Handshake';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { UserDetails } from '../../interfaces/User/UserDetails';
import {
  getUser,
  updateAffiliateProgramForUser,
  updateUser,
} from '../../api/User';
import { sharedStyles } from '../../utils/Style';
import { Waiting } from '../../components/Waiting/Waiting';
import NumberInput from '../../components/NumberInput/NumberInput';
import { UserRoleDropdown } from '../../components/Dropdowns/UserRoleDropdown';
import { UserRole } from '../../interfaces/User/UserRole';
import { CompanyDropdown } from '../../components/Dropdowns/CompanyDropdown';
import { UserPasswordWidget } from './UserPasswordWidget';
import { ConvertToAffiliateDialog } from './ConvertToAffiliateDialog';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';

export const UpdateUserPage = () => {
  const navigate = useNavigate();

  const { userID: userIDAsStr } = useParams<string>();

  const userID = userIDAsStr ? Number(userIDAsStr) : undefined;

  const [user, setUser] = useState<UserDetails | undefined>(undefined);
  const [showAffiliateDialog, setShowAffiliateDialog] = useState(false);
  const [showCancelAffiliateDialog, setShowCancelAffiliateDialog] =
    useState(false);

  const [loading, setLoading] = useState(false);

  const refreshUser = () => {
    setLoading(true);
    getUser(userID!)
      .then((u) => setUser(u))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refreshUser();
  }, [userID]);

  const handleSave = () => {
    if (!user) {
      return;
    }

    setLoading(true);
    updateUser(user)
      .then(() => {
        toast.success('Kullanıcı başarıyla güncellendi');
        refreshUser();
      })
      .catch((err) => {
        toast.error(err.message);
        setLoading(false);
      });
  };

  const handleCancelAffiliate = () => {
    if (!user) return;

    setShowCancelAffiliateDialog(false);
    setLoading(true);
    updateAffiliateProgramForUser(user.id)
      .then(() => {
        toast.success('Kullanıcının partner programı başarıyla sonlandırıldı');
        refreshUser();
      })
      .catch((err) => {
        toast.error(err.message);
        setLoading(false);
      });
  };

  const isAffiliateUser = !!user?.affiliateEnabled && !!user?.affiliateCode;

  const goBack = () => navigate('/users/users');

  return (
    <Box sx={{ ...sharedStyles.pageBody }}>
      <ConvertToAffiliateDialog
        user={user}
        open={showAffiliateDialog}
        onClose={() => setShowAffiliateDialog(false)}
        onConvert={() => {
          setShowAffiliateDialog(false);
          refreshUser();
        }}
      />
      {showCancelAffiliateDialog && (
        <ConfirmDialog
          content='Gerçekten kullanıcının partner programını bitirmek istiyor musunuz?'
          open={showCancelAffiliateDialog}
          onClose={() => setShowCancelAffiliateDialog(false)}
          onConfirm={handleCancelAffiliate}
          color='error'
        />
      )}
      <Waiting open={loading} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 3,
          gap: 1.5,
        }}
      >
        <IconButton onClick={goBack} sx={{ ...sharedStyles.backIcon }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant='h5'>Kullanıcıyı Düzenle</Typography>
      </Box>

      {!user && !loading && (
        <Typography>Kullanıcı yüklenirken bir hata oluştu</Typography>
      )}

      {user && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          <Paper
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: 'fit-content',
              width: 'fit-content',
              p: 3,
              gap: 1.5,
            }}
          >
            <Typography
              sx={{
                ...sharedStyles.h5,
                fontWeight: 'bold',
              }}
            >
              Kullanıcı Bilgileri
            </Typography>
            <Divider />

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                maxWidth: 800,
                gap: 1.5,
                flexWrap: 'wrap',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  flexGrow: 1,
                  minWidth: 300,
                }}
              >
                <TextField
                  label='ID'
                  variant='outlined'
                  size='small'
                  value={user.id.toString()}
                  disabled
                  fullWidth
                />

                <TextField
                  label='İsim'
                  variant='outlined'
                  size='small'
                  value={user.firstName}
                  onChange={(e) =>
                    setUser({ ...user, firstName: e.target.value })
                  }
                  fullWidth
                />

                <TextField
                  label='Soysim'
                  variant='outlined'
                  size='small'
                  value={user.lastName}
                  onChange={(e) =>
                    setUser({ ...user, lastName: e.target.value })
                  }
                  fullWidth
                />

                <TextField
                  label='E-Posta Adresi'
                  variant='outlined'
                  size='small'
                  value={user.email}
                  onChange={(e) => setUser({ ...user, email: e.target.value })}
                  fullWidth
                />

                <TextField
                  label='Telefon Numarası'
                  variant='outlined'
                  size='small'
                  value={user.phone}
                  onChange={(e) => setUser({ ...user, phone: e.target.value })}
                  fullWidth
                />

                <NumberInput
                  label='Puan'
                  value={user.point}
                  onChange={(newPoint) => setUser({ ...user, point: newPoint })}
                />

                <UserRoleDropdown
                  label='Rol'
                  value={user.role as UserRole}
                  onChange={(newRole) =>
                    setUser({ ...user, role: newRole ?? UserRole.USER })
                  }
                  required
                />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  flexGrow: 1,
                  minWidth: 300,
                }}
              >
                <CompanyDropdown
                  label='Bayi / Üye Grubu'
                  companyID={user.companyID}
                  onChange={(newID) => setUser({ ...user, companyID: newID })}
                />

                <TextField
                  label='Şehir'
                  variant='outlined'
                  size='small'
                  value={user.city}
                  disabled
                  fullWidth
                />

                <TextField
                  label='İlçe'
                  variant='outlined'
                  size='small'
                  value={user.state}
                  disabled
                  fullWidth
                />

                <TextField
                  label='Adres'
                  variant='outlined'
                  size='small'
                  multiline
                  minRows={5}
                  value={[user.addressLine1, user.addressLine2]
                    .filter(Boolean)
                    .join('\n')}
                  disabled
                  fullWidth
                />

                {isAffiliateUser && (
                  <TextField
                    label='Affiliate Kodu'
                    variant='outlined'
                    size='small'
                    value={user.affiliateCode ?? ''}
                    disabled
                    fullWidth
                  />
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5 }}>
              {isAffiliateUser ? (
                <Button
                  variant='outlined'
                  color='error'
                  startIcon={<BlockIcon />}
                  onClick={() => setShowCancelAffiliateDialog(true)}
                  sx={{ ...sharedStyles.buttonText }}
                >
                  Partner Programını Sonlandır
                </Button>
              ) : (
                <Button
                  variant='outlined'
                  startIcon={<HandshakeIcon />}
                  onClick={() => setShowAffiliateDialog(true)}
                  sx={{ ...sharedStyles.buttonText }}
                >
                  Partner Hesabına Çevir
                </Button>
              )}

              <Button
                variant='contained'
                onClick={handleSave}
                sx={{ ...sharedStyles.buttonText }}
              >
                Kaydet
              </Button>
            </Box>
          </Paper>

          <UserPasswordWidget email={user.email} />
        </Box>
      )}
    </Box>
  );
};
